import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { stripHtml } from 'string-strip-html';
import { FileTextIcon, InfoIcon } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogDescription,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

function plainTermsText(html) {
    return stripHtml(String(html || '')).result.replace(/\s+/g, ' ').trim();
}

const termsSchema = z.object({
    termsText: z
        .string()
        .refine(value => plainTermsText(value).length >= 40, {
            message: 'Informe os termos com pelo menos 40 caracteres',
        })
        .refine(value => String(value || '').length <= 200000, {
            message: 'Texto muito longo',
        }),
});

function DialogTermsClub({ data, mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const portalContainer = useClubPortalContainer();

    const form = useForm({
        resolver: zodResolver(termsSchema),
        defaultValues: { termsText: '' },
    });

    useEffect(() => {
        if (!open) return;
        form.reset({
            termsText: data?.termsText ? String(data.termsText) : '',
        });
    }, [open, data, form]);

    const onSubmit = async values => {
        try {
            await server.put('club', {
                id: data?.id,
                termsText: values.termsText?.trim() || null,
            });
            enqueueSnackbar('Termos e condições atualizados');
            await mutate();
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const { isSubmitting, errors } = form.formState;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent className="sm:max-w-3xl" container={portalContainer}>
                <ScrollDialogHeader>
                    <DialogTitle>Termos e condições</DialogTitle>
                    <DialogDescription>
                        Texto formatado exibido no site e no app do clube, com link nas telas de checkout.
                    </DialogDescription>
                </ScrollDialogHeader>

                <DialogBody>
                    <form id="club-terms-form" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                        <Alert variant="info">
                            <InfoIcon />
                            <AlertDescription>
                                Obrigatório para clubes que vendem assinaturas. Use títulos, listas e links quando
                                fizer sentido — o assinante precisa aceitar estes termos antes de concluir a compra.
                            </AlertDescription>
                        </Alert>

                        <FieldGroup>
                            <Field data-invalid={Boolean(errors.termsText)}>
                                <FieldLabel>Texto dos termos</FieldLabel>
                                <Controller
                                    name="termsText"
                                    control={form.control}
                                    render={({ field }) => (
                                        <RichTextEditor
                                            value={field.value}
                                            onChange={field.onChange}
                                            placeholder="Escreva ou cole os termos e condições do seu clube…"
                                            minHeight={360}
                                        />
                                    )}
                                />
                                <FieldDescription>
                                    Editor com formatação (negrito, listas, links, títulos). O HTML é salvo e exibido no
                                    site e no app.
                                </FieldDescription>
                                {errors.termsText ? (
                                    <FieldError>{errors.termsText.message}</FieldError>
                                ) : null}
                            </Field>
                        </FieldGroup>
                    </form>
                </DialogBody>

                <ScrollDialogFooter>
                    <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                        Cancelar
                    </Button>
                    <Button type="submit" form="club-terms-form" disabled={isSubmitting}>
                        {isSubmitting ? <Spinner className="size-4" /> : 'Salvar termos'}
                    </Button>
                </ScrollDialogFooter>
            </ScrollDialogContent>
        </Dialog>
    );
}

export default function TermsClub({ data, mutate }) {
    const { open, handleClose } = useDialog('#terms-club');
    const plain = plainTermsText(data?.termsText);
    const configured = plain.length > 0;
    const preview = configured
        ? plain.slice(0, 160) + (plain.length > 160 ? '…' : '')
        : 'Ainda não configurado — obrigatório para vender no site.';

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={FileTextIcon}
                        title="Termos e condições"
                        description="Conteúdo legal do clube para checkout web, página /termos e app do assinante."
                    />
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">Status</span>
                        <Badge variant={configured ? 'default' : 'destructive'}>
                            {configured ? 'Configurado' : 'Pendente'}
                        </Badge>
                    </div>
                    <div>
                        <span className="text-muted-foreground">Prévia</span>
                        <p className="font-medium text-muted-foreground">{preview}</p>
                    </div>
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#terms-club" />}>
                        {configured ? 'Editar termos' : 'Cadastrar termos'}
                    </Button>
                </CardFooter>
            </Card>

            <DialogTermsClub data={data} mutate={mutate} open={open} onOpenChange={isOpen => !isOpen && handleClose()} />
        </>
    );
}
